/**
 * What a plant costs, and what that price becomes once somebody has agreed to it.
 *
 * Two jobs that have to be described together because the second is what makes
 * the first safe.
 *
 * **Resolution** answers "what does this customer pay for this plant today".
 * The answer is not stored on the product - it has four inputs, none of them on
 * the product - so it is worked out: the customer's tier, then any override
 * that customer has on that product, both filtered to the day being asked
 * about. docs/02-data-model.md: "customer_price_overrides - customer x product
 * -> price, effective dates. Beats the tier."
 *
 * **Snapshotting** is what stops that answer moving. The moment a line is
 * captured, the price and the description are copied onto the line and the
 * line stops referring to the price list at all. docs/02: "A price list change
 * must never retro-edit a printed order." An order printed on Tuesday and
 * picked on Thursday is the same order, whatever admin did to the tier on
 * Wednesday, and the way that is guaranteed is that no read path anywhere goes
 * from a captured line back to `price_list_items`.
 *
 * ## Why this module resolves nothing by itself
 *
 * Everything here is a pure function over rows somebody else fetched.
 * `resolvePrice` takes the candidates rather than a customer id, and does no
 * I/O, because the rule it applies is the part that has to be identical on a
 * laptop in a shed and in whatever prices a Xero invoice later. The fetching -
 * which rows, from which mirror, with which indexes - is
 * `SqlitePricingStore`'s, and it can change without the rule changing.
 *
 * ## Two prices effective on the same day
 *
 * Postgres has no exclusion constraint on either pricing table, so overlapping
 * date ranges are possible: two rows for one tier and one product, both live
 * today. That is a data-entry mistake rather than a state to model, but a
 * mistake that made two devices disagree about a price would be far worse than
 * the mistake itself, so the tie-break is total and deterministic and every
 * device runs it over the same rows:
 *
 * 1. the later `effective_from` wins - the most recently begun price;
 * 2. then the narrower window - a dated end beats an open one, and an earlier
 *    end beats a later one, because a short-dated price is the more specific
 *    statement;
 * 3. then the lower `id`, which decides nothing sensible but decides it the
 *    same way everywhere.
 *
 * The losers come back in `alsoEffective` rather than being swallowed, so a
 * price-list screen can say "two prices are live on this product today" instead
 * of leaving somebody to discover it from an invoice.
 *
 * ## Tax
 *
 * `documentTotals` takes a rate and has no default. docs/00-scope.md is
 * explicit that this is not an accounting application and that Xero remains the
 * accounting system; the VAT treatment - the rate, whether prices are quoted
 * inclusive or exclusive, whether any line is zero-rated - has not been decided
 * with the client. A plausible-looking 15% wired in here would be a number on a
 * customer's quote that nobody chose, which is worse than an argument.
 */
import { ZERO_CENTS, addCents, cents, multiplyByQuantity, percentOf, subtractCents, sumCents, } from './money.js';
import { formatQuantity, thousandthsOf } from './quantity.js';
import { err, ok } from './result.js';
import {} from './customer.js';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Shape and a real day. 31 February is a transcription error, not a date. */
function malformedDate(value) {
    if (!ISO_DATE.test(value))
        return true;
    const at = new Date(`${value}T00:00:00.000Z`);
    return Number.isNaN(at.getTime()) || at.toISOString().slice(0, 10) !== value;
}
/** Today, as the columns spell a day. The only clock this module reads. */
export function today(now = new Date()) {
    return now.toISOString().slice(0, 10);
}
/**
 * Is this row live on that day?
 *
 * Both ends inclusive. `effective_from` defaults to `current_date` on the
 * server, so a price entered this morning applies this morning; and a row whose
 * `effective_to` is today is a price on its last day, not a price that expired
 * at midnight. ISO-8601 dates compare correctly as strings, which is why the
 * mirror keeps them as text.
 */
function liveOn(candidate, asAt) {
    if (candidate.effectiveFrom > asAt)
        return false;
    return candidate.effectiveTo === undefined || candidate.effectiveTo >= asAt;
}
/**
 * The total order described at the top of this file.
 *
 * Negative means `a` wins, so the winner sorts first.
 */
function preferred(a, b) {
    if (a.effectiveFrom !== b.effectiveFrom) {
        return a.effectiveFrom > b.effectiveFrom ? -1 : 1;
    }
    if (a.effectiveTo !== b.effectiveTo) {
        if (a.effectiveTo === undefined)
            return 1;
        if (b.effectiveTo === undefined)
            return -1;
        return a.effectiveTo < b.effectiveTo ? -1 : 1;
    }
    if (a.id === b.id)
        return 0;
    return a.id < b.id ? -1 : 1;
}
function best(candidates, asAt) {
    const live = candidates.filter((candidate) => liveOn(candidate, asAt));
    if (live.length === 0)
        return undefined;
    const [winner, ...rest] = [...live].sort(preferred);
    if (winner === undefined)
        return undefined;
    return { winner, alsoEffective: rest.map((candidate) => candidate.id) };
}
/**
 * The price, from the rows this device holds.
 *
 * An override beats a tier outright - not the lower of the two, not the later
 * of the two. A customer with a negotiated price pays that price even when the
 * tier is cheaper, because the negotiated price is a promise somebody made and
 * a system that quietly improved on it would be a system that could quietly
 * worsen it too.
 */
export function resolvePrice(input) {
    if (malformedDate(input.asAt)) {
        throw new TypeError(`asAt must be an ISO-8601 date, got ${JSON.stringify(input.asAt)}`);
    }
    const tier = best(input.tierPrices, input.asAt);
    const override = best(input.overrides, input.asAt);
    if (override !== undefined) {
        return {
            found: true,
            source: 'override',
            priceCents: override.winner.priceCents,
            priceId: override.winner.id,
            effectiveFrom: override.winner.effectiveFrom,
            ...(override.winner.effectiveTo === undefined
                ? {}
                : { effectiveTo: override.winner.effectiveTo }),
            ...(tier === undefined ? {} : { listPriceCents: tier.winner.priceCents }),
            alsoEffective: override.alsoEffective,
        };
    }
    if (tier !== undefined) {
        return {
            found: true,
            source: 'tier',
            priceCents: tier.winner.priceCents,
            priceId: tier.winner.id,
            effectiveFrom: tier.winner.effectiveFrom,
            ...(tier.winner.effectiveTo === undefined
                ? {}
                : { effectiveTo: tier.winner.effectiveTo }),
            alsoEffective: tier.alsoEffective,
        };
    }
    return { found: false, reason: input.hasTier ? 'no-price' : 'no-tier' };
}
/**
 * A dated price, checked against migration 0001's constraints.
 *
 * `price_cents >= 0` and `effective_to is null or effective_to >= effective_from`
 * are `check` constraints on both tables, restated here so that a rep is told
 * about a back-to-front date range while typing it rather than by a rejection
 * an hour later. Postgres is still the control.
 *
 * A price of zero is allowed, because the column allows it and a nursery does
 * give plants away - samples, replacements, a goodwill line on a complaint. A
 * negative one is not: that is a credit, and a credit is a document, not a
 * price.
 */
export function validatePrice(draft, now = () => today()) {
    const problems = [];
    if (!Number.isSafeInteger(draft.priceCents)) {
        problems.push({
            field: 'priceCents',
            kind: 'not-an-integer',
            message: 'A price is a whole number of cents.',
        });
    }
    else if (draft.priceCents < 0) {
        problems.push({
            field: 'priceCents',
            kind: 'negative',
            message: 'A price cannot be negative. A credit is a document, not a price.',
        });
    }
    const from = draft.effectiveFrom?.trim();
    const to = draft.effectiveTo?.trim();
    const effectiveFrom = from === undefined || from.length === 0 ? now() : from;
    if (malformedDate(effectiveFrom)) {
        problems.push({
            field: 'effectiveFrom',
            kind: 'malformed',
            message: 'The start date must be a real day, written as YYYY-MM-DD.',
        });
    }
    const effectiveTo = to === undefined || to.length === 0 ? undefined : to;
    if (effectiveTo !== undefined && malformedDate(effectiveTo)) {
        problems.push({
            field: 'effectiveTo',
            kind: 'malformed',
            message: 'The end date must be a real day, written as YYYY-MM-DD.',
        });
    }
    if (problems.length === 0 &&
        effectiveTo !== undefined &&
        effectiveTo < effectiveFrom) {
        problems.push({
            field: 'effectiveTo',
            kind: 'out-of-order',
            message: 'The end date cannot be before the start date.',
        });
    }
    if (problems.length > 0)
        return err(problems);
    return ok({
        priceCents: cents(draft.priceCents),
        effectiveFrom,
        ...(effectiveTo === undefined ? {} : { effectiveTo }),
    });
}
export function priceListItemColumns(price, tierId, productId) {
    return {
        tier_id: tierId,
        product_id: productId,
        price_cents: price.priceCents,
        effective_from: price.effectiveFrom,
        ...(price.effectiveTo === undefined ? {} : { effective_to: price.effectiveTo }),
    };
}
export function priceOverrideColumns(price, customerId, productId) {
    return {
        customer_id: customerId,
        product_id: productId,
        price_cents: price.priceCents,
        effective_from: price.effectiveFrom,
        ...(price.effectiveTo === undefined ? {} : { effective_to: price.effectiveTo }),
    };
}
/** The one column on either pricing table a form can empty. */
export const CLEARABLE_PRICE_COLUMNS = ['effective_to'];
/**
 * A cap on the description, for the paper reason `product.ts` gives.
 *
 * The line description is what prints on a picking slip and on the Xero
 * invoice. `quote_lines.description` is `text` and would take a pasted
 * paragraph without complaint, and nobody would find out until a trolley.
 */
const MAX_DESCRIPTION = 200;
/**
 * Gross, before the line discount: the unit price times the quantity.
 *
 * The multiplication is `multiplyByQuantity`'s, which runs in BigInt and rounds
 * half away from zero, because plants sell in fractional trays and a price
 * times 2.5 has to land on a cent the same way every time.
 */
export function lineGross(unitPriceCents, quantity) {
    return multiplyByQuantity(unitPriceCents, quantity);
}
export function validateLine(draft) {
    const problems = [];
    const description = draft.description.trim();
    if (description.length === 0) {
        problems.push({
            field: 'description',
            kind: 'required',
            message: 'A line needs a description. It is what prints on the slip.',
        });
    }
    if (description.length > MAX_DESCRIPTION) {
        problems.push({
            field: 'description',
            kind: 'too-long',
            message: `The description must be ${String(MAX_DESCRIPTION)} characters or fewer.`,
        });
    }
    // `quantity > 0` is a check constraint on both line tables. A line for
    // nothing is a line somebody meant to delete.
    if (thousandthsOf(draft.quantity) <= 0) {
        problems.push({
            field: 'quantity',
            kind: 'not-positive',
            message: 'A quantity must be more than zero. To remove a line, delete it.',
        });
    }
    if (draft.unitPriceCents < 0) {
        problems.push({
            field: 'unitPriceCents',
            kind: 'negative',
            message: 'A unit price cannot be negative.',
        });
    }
    const discountCents = draft.discountCents ?? ZERO_CENTS;
    if (discountCents < 0) {
        problems.push({
            field: 'discountCents',
            kind: 'negative',
            message: 'A discount cannot be negative. To add to a price, change the price.',
        });
    }
    if (problems.length > 0)
        return err(problems);
    const gross = lineGross(draft.unitPriceCents, draft.quantity);
    // A discount larger than the line is how a line total goes negative, and a
    // negative line on an order is a credit note wearing the wrong clothes. The
    // column would take it - `line_total_cents` carries no check - which is
    // exactly why it is refused here.
    if (discountCents > gross) {
        return err([
            {
                field: 'discountCents',
                kind: 'exceeds',
                message: `A discount of ${formatCentsPlain(discountCents)} is more than the line ` +
                    `is worth (${formatQuantity(draft.quantity)} at ` +
                    `${formatCentsPlain(draft.unitPriceCents)} is ` +
                    `${formatCentsPlain(gross)}).`,
            },
        ]);
    }
    return ok({
        description,
        quantity: draft.quantity,
        unitPriceCents: draft.unitPriceCents,
        discountCents,
        lineTotalCents: subtractCents(gross, discountCents),
        ...(draft.productId === undefined ? {} : { productId: draft.productId }),
        ...(draft.batchId === undefined ? {} : { batchId: draft.batchId }),
    });
}
/**
 * Plain rand for a message, without the currency formatting.
 *
 * `formatZAR` puts a thin space in the grouping and belongs on a screen.
 * Inside a validation message it would make the sentence depend on the
 * formatter, and these messages are asserted in tests.
 */
function formatCentsPlain(amount) {
    const negative = amount < 0;
    const magnitude = Math.abs(amount);
    return `${negative ? '-' : ''}R${String(Math.trunc(magnitude / 100))}.${String(magnitude % 100).padStart(2, '0')}`;
}
export function quoteLineColumns(line, quoteId, lineNo) {
    return {
        quote_id: quoteId,
        line_no: lineNo,
        description: line.description,
        quantity: quantityColumn(line.quantity),
        unit_price_cents: line.unitPriceCents,
        discount_cents: line.discountCents,
        line_total_cents: line.lineTotalCents,
        ...(line.productId === undefined ? {} : { product_id: line.productId }),
        ...(line.batchId === undefined ? {} : { batch_id: line.batchId }),
    };
}
export function orderLineColumns(line, orderId, lineNo) {
    return {
        order_id: orderId,
        line_no: lineNo,
        description: line.description,
        quantity_ordered: quantityColumn(line.quantity),
        unit_price_cents: line.unitPriceCents,
        discount_cents: line.discountCents,
        line_total_cents: line.lineTotalCents,
        ...(line.productId === undefined ? {} : { product_id: line.productId }),
        ...(line.batchId === undefined ? {} : { batch_id: line.batchId }),
    };
}
/**
 * A quantity as `numeric(14,3)` sees it.
 *
 * Sent as a fixed three-decimal string rather than a number, because a number
 * on the wire is a float by the time PostgREST has parsed it, and a float near
 * a quantity is the mistake `quantity.ts` exists to prevent. Postgres casts the
 * text to `numeric` exactly.
 */
function quantityColumn(quantity) {
    const negative = quantity < 0;
    const magnitude = Math.abs(thousandthsOf(quantity));
    return `${negative ? '-' : ''}${String(Math.trunc(magnitude / 1000))}.${String(magnitude % 1000).padStart(3, '0')}`;
}
/**
 * The four header figures, from the lines.
 *
 * The arrangement is the one that cannot double-count: **subtotal is net**,
 * `discount_cents` is carried alongside so a document can print "you saved
 * R240.00", and `total = subtotal + tax`. The other convention in circulation -
 * gross subtotal, then subtract the discount - reaches the same total, but only
 * if every reader agrees on which it is looking at, and the day one does not is
 * the day a discount comes off twice. One invariant, checkable in a test:
 * subtotal plus tax is the total, always.
 *
 * `taxBasisPoints` has no default on purpose. See the note on tax at the top of
 * this file.
 */
export function documentTotals(lines, taxBasisPoints) {
    if (!Number.isSafeInteger(taxBasisPoints) || taxBasisPoints < 0) {
        throw new TypeError(`taxBasisPoints must be a non-negative integer, got ${taxBasisPoints}`);
    }
    const subtotalCents = sumCents(lines.map((line) => line.lineTotalCents));
    const discountCents = sumCents(lines.map((line) => line.discountCents));
    const taxCents = percentOf(subtotalCents, taxBasisPoints);
    return {
        subtotalCents,
        discountCents,
        taxCents,
        totalCents: addCents(subtotalCents, taxCents),
    };
}
/** The header columns a quote or an order carries, from its totals. */
export function totalsColumns(totals) {
    return {
        subtotal_cents: totals.subtotalCents,
        discount_cents: totals.discountCents,
        tax_cents: totals.taxCents,
        total_cents: totals.totalCents,
    };
}
//# sourceMappingURL=pricing.js.map